import { useContext } from 'react';
import { ProductType } from 'src/easyclaim.api.client.typescript/src';
import {
	IconCheck,
	IconDescription,
	IconInvalid,
	IconSearch,
	IconTime,
	IconUpload,
	MemoIconPhotoUploadPlus,
	PlaceholderDashboardV2_2,
	PlaceholderFrontLeftV2,
	PlaceholderFrontRightV2,
	PlaceholderOverallDashboard,
	PlaceholderRearLeftV2,
	PlaceholderRearRightV2,
	PlaceholderVinV2,
	PlaceholderWindshield,
} from 'src/easyclaim.app.assets';
import { MemoPlaceholderOverallDashboardOutside } from 'src/easyclaim.app.assets/artwork/placeholder/v2/PlaceholderOverallDashboardOutside';
import { MemoPlaceholderVinOnly } from 'src/easyclaim.app.assets/artwork/placeholder/v2/PlaceholderVinOnly';
import GlobeTransparent from 'src/easyclaim.app.assets/icons/GlobeTransparent';
import { IconSlimCheck } from 'src/easyclaim.app.assets/icons/IconSlimCheck';
import { SlimCameraIcon } from 'src/easyclaim.app.assets/icons/SlimCameraIcon';
import { SlimDocumentIcon } from 'src/easyclaim.app.assets/icons/SlimDocumentIcon';
import { IconSlimInvalid } from 'src/easyclaim.app.assets/icons/SlimInvalid';
import { SlimMagnifierIcon } from 'src/easyclaim.app.assets/icons/SlimMagnifierIcon';
import CustomerConfigurationContext from 'src/easyclaim.app.contexts/CustomerConfigurationContextCore';
import theme from 'src/easyclaim.app.theme/theme';

import { FlagIcon } from './FlagIcon';

export const DamageDescriptionIcon = ({
	color,
	height,
}: { color?: string; height?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <SlimDocumentIcon height={'1.7rem'} />;
	}

	return <IconDescription height={height} color={color} />;
};

export const ReviewIcon = ({ color, height }: { color?: string; height?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <IconSlimCheck height={'2rem'} />;
	}
	if (
		configuration?.layout === ProductType.PolicyCheck &&
		(configuration?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
			configuration?.companyShortName?.trim().toLowerCase() === 'pevno')
	) {
		return <p>3</p>;
	}

	return <IconCheck height={height} color={color} />;
};

export const DamageImagesIcon = ({ color, height }: { color?: string; height?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <SlimMagnifierIcon height={'1.7rem'} />;
	}

	return <IconSearch height={height} color={color} />;
};

export const FourDiagonalPerspectivesIcon = ({
	color,
	height,
}: { color?: string; height?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <SlimCameraIcon height={'1.7rem'} />;
	}
	if (
		configuration?.layout === ProductType.PolicyCheck &&
		(configuration?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
			configuration?.companyShortName?.trim().toLowerCase() === 'pevno')
	) {
		return <p>1</p>;
	}

	return <MemoIconPhotoUploadPlus height={height} color={color} />;
};

export const FourAdditionalPicturesIcon = ({
	color,
	height,
}: { color?: string; height?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.PolicyCheck &&
		(configuration?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
			configuration?.companyShortName?.trim().toLowerCase() === 'pevno')
	) {
		return <p>2</p>;
	}

	return <IconUpload height={height} color={color} />;
};

export const TimeoutIcon = ({ color }: { color?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <IconSlimInvalid height={'1.7rem'} color={color} />;
	}
	if (
		configuration?.layout === ProductType.PolicyCheck &&
		(configuration?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
			configuration?.companyShortName?.trim().toLowerCase() === 'pevno')
	) {
		return <IconSlimInvalid height={'1.7rem'} color={color} />;
	}

	return <IconTime />;
};

export const ReviewCanMoveNextIcon = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <IconSlimCheck />;
	}

	return <IconCheck height={'1.5rem'} color={theme.colors.success} />;
};

export const ReviewCanNotMoveNextIcon = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.layout === ProductType.EasyClaim &&
		configuration?.companyShortName?.trim().toLowerCase() === 'allianzpt'
	) {
		return <IconSlimInvalid height={'1.5rem'} />;
	}

	return <IconInvalid height={'1.5rem'} color={theme.colors.danger} />;
};

export const CarFrontLeftPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/PlaceholderFrontLeft.jpg"
				alt="Front Left Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderFrontLeftV2 className="photo-placeholder" />;
};

export const CarFrontRightPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/PlaceholderFrontRight.jpg"
				alt="Front Right Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderFrontRightV2 className="photo-placeholder" />;
};

export const CarRearLeftPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/PlaceholderRearLeft.jpg"
				alt="Rear Left Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderRearLeftV2 className="photo-placeholder" />;
};

export const CarRearRightPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/PlaceholderRearRIght.jpg"
				alt="Rear Right Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderRearRightV2 className="photo-placeholder" />;
};

export const VinPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
		configuration?.companyShortName?.trim().toLowerCase() === 'pevno'
	) {
		return <MemoPlaceholderVinOnly className="photo-placeholder" />;
	}
	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/VinPlaceholder.png"
				alt="Vin Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderVinV2 className="photo-placeholder" />;
};

export const CarDamagePlaceholder = () => {
	return (
		<img
			src="/images/Solvd/CarDamagePlaceholder.png"
			alt="Car Placeholder"
			className="photo-placeholder"
		/>
	);
};

export const OverallDashboardPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (
		configuration?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
		configuration?.companyShortName?.trim().toLowerCase() === 'pevno'
	) {
		return <MemoPlaceholderOverallDashboardOutside className="photo-placeholder" />;
	}
	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/DashboardPlaceholder.png"
				alt="Overall Dashboard Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderOverallDashboard className="photo-placeholder" />;
};

export const OdometerPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/OdometerPlaceholder.png"
				alt="Odometer Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderDashboardV2_2 className="photo-placeholder" />;
};

export const WindshieldPlaceholder = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/WindshieldPlaceholder.png"
				alt="Windshield Placeholder"
				className="photo-placeholder"
			/>
		);
	}

	return <PlaceholderWindshield className="photo-placeholder" />;
};

export const ChangeLanguageIcon = ({ currentLanguage }: { currentLanguage?: string } = {}) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return <FlagIcon flagCountryCode={currentLanguage!}></FlagIcon>;
	}

	return <GlobeTransparent height="1rem" width="1rem" />;
};

export const VinHintPlaceholder = ({ id }: { id?: string }) => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/VinHint.png"
				alt="Vin Localization"
				id={id}
				className="photo-placeholder"
			/>
		);
	}

	return null;
};

export const HandWithSolvdCJIcon = () => {
	const { configuration } = useContext(CustomerConfigurationContext);

	if (configuration?.useSolvdStyles) {
		return (
			<img
				src="/images/Solvd/HandWithSolvdCJ.png"
				alt="CJ miniature"
				className="hand-with-solvd-cj"
			/>
		);
	}

	return null;
};
